/**
 * Content Type Icon Mapping
 *
 * Maps content types to their corresponding design system icons
 */

import { Chat, Photo, Video, Speakers, Message, Label, Profile, CalendarSolid } from '@soteria/design-system-ve';
import type { ComponentType } from 'react';

import type { ContentType } from './contentDetection';

/**
 * Icon mapping for different content types in moderation cards
 */
export const CONTENT_TYPE_ICONS: Record<string, ComponentType> = {
  text: Chat,
  image: Photo,
  video: Video,
  audio: Speakers,
  comment: Message,
  labels: Label,
  username: Profile,
  timestamp: CalendarSolid,
} as const;

/**
 * Gets the icon component for a specific content type
 *
 * @param contentType - The content type to get icon for
 * @returns Icon component or null if not found
 *
 * @example
 * ```tsx
 * const TextIcon = getContentTypeIcon('text');
 * return <Icon icon={TextIcon} />;
 * ```
 */
export function getContentTypeIcon(contentType: ContentType | string): ComponentType | null {
  return CONTENT_TYPE_ICONS[contentType] || null;
}

/**
 * Gets text icon (Chat)
 */
export function getTextIcon(): ComponentType {
  return Chat;
}

/**
 * Gets image icon (Photo)
 */
export function getImageIcon(): ComponentType {
  return Photo;
}

/**
 * Gets comment icon (Message)
 */
export function getCommentIcon(): ComponentType {
  return Message;
}

/**
 * Gets labels icon (Label)
 */
export function getLabelsIcon(): ComponentType {
  return Label;
}

/**
 * Gets username icon (Profile)
 */
export function getUsernameIcon(): ComponentType {
  return Profile;
}

/**
 * Gets timestamp icon (CalendarSolid)
 */
export function getTimestampIcon(): ComponentType {
  return CalendarSolid;
}

/**
 * Gets audio icon (Speakers)
 */
export function getAudioIcon(): ComponentType {
  return Speakers;
}
